import apiClient from '../../api/http-common';
import { Endpoints } from '../constants';

export interface IDairyBank {
  filtered: number;
  rows: IBank[];
  total: number;
}

export interface IBank {
  id: string;
  name: string;
  accounts?: IBankAccountBad[];
  status?: string;
}

export interface IBankAccount {
  bank_id?: string;
  id?: string;
  iban: string;
  swift: string;
  depot_no: string;
  depot_name: string;
}

export interface IBankAccountBad {
  bankId: string;
  depotName: string;
  depotNo: string;
  iban: string;
  id: string;
  otc: boolean | number;
  status: boolean | number;
  swift: string;
}

export interface ILocation {
  id: string;
}

export interface ILocationOptionData {
  countries: ICountry[];
  officeTypes: IOfficeType[];
}

interface ICountry {
  id: number;
  iso: string;
  phoneCode: string;
  label: string;
}

interface IOfficeType {
  key: string;
  label: string;
}

const getDairyProfile = async () => {
  const response = await apiClient.post(`${Endpoints.DAIRY}/show`);

  return response.data;
};

// --- START - LOCATIONS API --- //

const getDairyLocations = (data?, signal?: AbortSignal) =>
  apiClient.post(`${Endpoints.LOCATIONS}/index`, data, { signal } );

const getDairyLocation = (id: string) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/edit`);

const showDairyLocation = (id: string) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/show`);

const createLocation = async (data: ILocation) =>
  apiClient.post(`${Endpoints.LOCATIONS}/store`, data);

const updateLocation = async (id: string, data: ILocation) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/update`, data);

const deleteLocation = async (id: string) =>
  apiClient.post(`${Endpoints.LOCATIONS}/${id}/delete`);

const getLocationOptiondata = async (): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.LOCATIONS}/get-options-data`);

  return response.data;
};

// --- END - LOCATIONS API --- //

// --- START - FARMERS API --- //

const countAllFarmers = () =>
  apiClient.post('/farmers/count-all');

const countAllActiveFarmers = () =>
  apiClient.post('/farmers/count-active');

// --- END - FARMERS API --- //

// --- START - FARMER GROUPS API --- //
const countAllFarmerGroups = () =>
  apiClient.post(`${Endpoints.FARMER_GROUPS}/count-all`);

const countAllActiveFarmerGroups = () =>
  apiClient.post(`${Endpoints.FARMER_GROUPS}/count-active`);

// --- END - FARMER GROUPS API --- //

// --- START - FARMER USERS API --- //
const countAllUsers = () =>
  apiClient.post('/users/count-all');

const countAllActiveUsers = () =>
  apiClient.post('/farmers/users/count-active');

const showUserData = (id: string) =>
  apiClient.post(`/users/${id}/show`);

// --- END - FARMER USERS API --- //

// --- START - BANKS API --- //
const getDairyBanks = async () => {
  const { data } = await apiClient.post<IDairyBank>(`${Endpoints.BANKS}/index`);
  return data;
};

const deleteDairyBankById = async (id: string): Promise<null> => {
  const { data } = await apiClient.post(`${Endpoints.BANKS}/${id}/delete`);

  return data;
};

const getDairyBankById = async (id: string): Promise<IBank> => {
  const { data } = await apiClient.post<IBank>(`${Endpoints.BANKS}/${id}/edit`);

  return data;
};

const createDairyBank = async (name: any): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.BANKS}/store`, name);

  return data;
};

const updateDairyBank = async ({ id, bankName }): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.BANKS}/${id}/update`, bankName);

  return data;
};

const createBankAccount = async (data) => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/store`, data);

  return response.data;
};

const editBankAccount = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/update`, data);

  return response.data;
};

const getDairyBankAccountById = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/edit`);

  return response.data;
};

const disableDairyAccount = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/disable`);

  return response.data;
};

const enableDairyAccount = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.BANK_ACCOUNTS}/${id}/enable`);

  return response.data;
};
// --- END - BANKS API --- //

// --- START - DISTRICT API --- //

const getDairyStates = async () => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/index`);
  return data;
};

const deleteDairyStateById = async (id: string): Promise<null> => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/${id}/delete`);

  return data;
};

const getDairyStateById = async (id: string): Promise<IBank> => {
  const { data } = await apiClient.post<IBank>(`${Endpoints.STATES}/${id}/edit`);

  return data;
};

const createDairyState = async (name: any): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/store`, name);

  return data;
};

const updateDairyState = async ({ id, bankName }): Promise<any> => {
  const { data } = await apiClient.post(`${Endpoints.STATES}/${id}/update`, bankName);

  return data;
};

const createStateDistrict = async (data) => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/store`, data);

  return response.data;
};

const getDistrictOptions = async () => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/get-options-data`);

  return response.data;
};

const editStateDistrict = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/${id}/update`, data);

  return response.data;
};

const getDairyStateDistrictById = async (id): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.DISTRICT}/${id}/edit`);

  return response.data;
};

const deleteStateDistrict = (id: string) =>
  apiClient.post(`${Endpoints.DISTRICT}/${id}/delete`)

// --- END - DISTRICT API --- //

// --- START - MILK TYPES API --- //

const getMilkTypes = () =>
  apiClient.post(`${Endpoints.MILK_TYPES}/index`)

const createMilkType = (data: any) =>
  apiClient.post(`${Endpoints.MILK_TYPES}/store`, data)

const updateMilkType = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.MILK_TYPES}/${id}/update`, data)

  return response.data;
};

const editMilkType = (id: string) =>
  apiClient.post(`${Endpoints.MILK_TYPES}/${id}/edit`)

const deleteMilkType = (id: string) =>
  apiClient.post(`${Endpoints.MILK_TYPES}/${id}/delete`)

// --- END - MILK TYPES API --- //

// --- START - HUSBANDRY API --- //

const getHusbandryForms = (data?) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/index`, data);

const editHusbandryForm = (id: string) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/${id}/edit`);

const createHusbandryForm = async (data) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/store`, data);

const updateHusbandryForm = async ({ id, data }): Promise<any> => {
  const response = await apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/${id}/update`, data)

  return response.data;
};

const deleteHusbandryForm = async (id: string) =>
  apiClient.post(`${Endpoints.HUSBANDRY_FORMS}/${id}/delete`);

// --- END - HUSBANDRY API --- //

const getSegmentationFile = (id: number) =>
  apiClient.post(`/segmentation/files/${id}/get-file`)

const getDairyLogo = async (data: string) => {
  const response = await apiClient.post('/dairies/get-file', { fileName: data });
  return response.data;
};

const DairyService = {
  getDairyProfile,
  getDairyLogo,
  getDairyBanks,
  createDairyBank,
  updateDairyBank,
  getDairyBankById,
  deleteDairyBankById,
  createBankAccount,
  editBankAccount,
  createLocation,
  getLocationOptiondata,
  getDairyLocations,
  getDairyLocation,
  updateLocation,
  deleteLocation,
  getDairyBankAccountById,
  disableDairyAccount,
  enableDairyAccount,
  countAllFarmerGroups,
  countAllFarmers,
  countAllUsers,
  showDairyLocation,
  showUserData,
  countAllActiveFarmerGroups,
  countAllActiveFarmers,
  countAllActiveUsers,
  getDairyStateById,
  getDairyStates,
  getDairyStateDistrictById,
  updateDairyState,
  deleteDairyStateById,
  createStateDistrict,
  createDairyState,
  editStateDistrict,
  getDistrictOptions,
  deleteStateDistrict,
  getMilkTypes,
  createMilkType,
  updateMilkType,
  editMilkType,
  deleteMilkType,
  getHusbandryForms,
  editHusbandryForm,
  deleteHusbandryForm,
  updateHusbandryForm,
  createHusbandryForm,
  getSegmentationFile
};

export default DairyService;
